import { useEffect, useMemo, useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import { Divider, Loader, Modal, Select } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  EMPLOYEE_REMOVAL_PREVIEW,
  GET_EMPLOYEES_ONLY,
  REMOVE_EMPLOYEE,
} from 'src/graphql/employees.graphql'
import IconTrash from 'src/icons/IconTrash'

interface RemoveUserModalProps {
  opened: boolean
  close: () => void
  employee: {
    employeeId: string
    firstName: string
    lastName: string
  } | null
  refetchEmployees: () => void
}

const RemoveUserModal = ({
  opened,
  close,
  employee,
  refetchEmployees,
}: RemoveUserModalProps) => {
  const [reassignToId, setReassignToId] = useState<string | null>(null)

  const [fetchPreview, { data: previewData, loading: previewLoading }] =
    useLazyQuery(EMPLOYEE_REMOVAL_PREVIEW)

  const { data: employeesData } = useQuery(GET_EMPLOYEES_ONLY, {
    variables: { pageInfo: { limit: 200, offset: 0 } },
    skip: !opened,
  })

  const [removeEmployee, { loading: removing }] = useMutation(
    REMOVE_EMPLOYEE,
    {
      refetchQueries: [GET_EMPLOYEES_ONLY],
      onCompleted: () => {
        toast('User removal started. Reassignments are being processed.', 'success')
        refetchEmployees()
        handleClose()
      },
      onError: (error) => {
        toast(error.message || 'Failed to remove user', 'error')
      },
    }
  )

  useEffect(() => {
    if (opened && employee) {
      fetchPreview({ variables: { employeeId: employee.employeeId } })
      setReassignToId(null)
    }
  }, [opened, employee, fetchPreview])

  const handleClose = () => {
    setReassignToId(null)
    close()
  }

  const handleRemove = () => {
    if (!employee || !reassignToId) return
    removeEmployee({
      variables: {
        input: {
          employeeId: employee.employeeId,
          reassignToEmployeeId: reassignToId,
        },
      },
    })
  }

  const reassigneeOptions = useMemo(() => {
    if (!employeesData?.employees?.items || !employee) return []
    return employeesData.employees.items
      .filter((e) => e.employeeId !== employee.employeeId)
      .map((e) => ({
        value: e.employeeId,
        label: `${e.firstName} ${e.lastName}`,
      }))
  }, [employeesData, employee])

  const preview = previewData?.employeeRemovalPreview

  const impactItems = useMemo(() => {
    if (!preview) return []
    const items = [
      { count: preview.leadApplicantCount, label: 'lead applicant', action: 'reassigned' },
      { count: preview.memberApplicantCount, label: 'member assignment', action: 'reassigned' },
      { count: preview.openTaskCount, label: 'task', action: 'reassigned' },
      { count: preview.activePermissionCount, label: 'permission', action: 'revoked' },
      { count: preview.unreadNotificationCount, label: 'notification', action: 'cleared' },
      { count: preview.voeEmailCount, label: 'VoE email', action: 'reassigned' },
      { count: preview.adverseActionCaseCount, label: 'adverse action case', action: 'reassigned' },
      { count: preview.jobListingCount, label: 'job listing', action: 'reassigned' },
      { count: preview.voeAssignmentCount, label: 'VoE assignment', action: 'cleared' },
      { count: preview.packageOrderCount, label: 'package order', action: 'cleared' },
    ]
    return items
      .filter((item) => item.count > 0)
      .map(
        ({ count, label, action }) =>
          `${count} ${label}${count !== 1 ? 's' : ''} ${action}`
      )
  }, [preview])

  const employeeName = employee
    ? `${employee.firstName} ${employee.lastName}`
    : ''

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="lg"
      withCloseButton={false}
    >
      <div className="flex flex-col gap-4">
        <IconTrash className="h-10 w-10 rounded bg-doubleNickel-error-50 fill-doubleNickel-white stroke-doubleNickel-error-600 p-2" />

        <div className="flex flex-col items-start">
          <div className="text-base font-semibold text-doubleNickel-gray-900">
            Remove {employeeName}?
          </div>
          <div className="text-sm text-doubleNickel-gray-700">
            This will archive the user, revoke all permissions, and reassign
            their work to another team member.
          </div>
        </div>

        {previewLoading ? (
          <div className="flex justify-center py-4">
            <Loader size="sm" />
          </div>
        ) : (
          <>
            {preview && (
              <div className="rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-3">
                <div className="mb-2 text-xs font-medium text-doubleNickel-gray-700">
                  Impact summary
                </div>
                {impactItems.length > 0 ? (
                  <ul className="m-0 list-inside list-disc space-y-1">
                    {impactItems.map((item) => (
                      <li
                        key={item}
                        className="text-xs text-doubleNickel-gray-600"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-doubleNickel-gray-600">
                    No active assignments found.
                  </div>
                )}
              </div>
            )}

            <Select
              label="Reassign all assignments to"
              placeholder="Select a team member"
              data={reassigneeOptions}
              value={reassignToId}
              onChange={setReassignToId}
              searchable
              withAsterisk
            />
          </>
        )}

        <Divider />

        <div className="flex w-full flex-row justify-end gap-3">
          <DNButton variant="outline" onClick={handleClose}>
            Cancel
          </DNButton>
          <DNButton
            variant="error"
            onClick={handleRemove}
            loading={removing}
            disabled={!reassignToId || previewLoading || removing}
            className="min-w-24"
          >
            Remove User
          </DNButton>
        </div>
      </div>
    </Modal>
  )
}

export default RemoveUserModal
